// music-catalog.ts — the one read the music player makes.
//
// The whole playable library, grouped by bucket (ft_music_catalog(), options/api/music_catalog_inc.php).
// Fetched once per catalog REVISION: sidelog.php stamps the current rev on the deck wrapper, and the
// `?rev=` answer is immutable in the browser cache, so every later page load is served locally until the
// next MusicForge ingest changes the rev. Every playlist is then resolved from this in music-resolver.ts —
// there is no per-change music request (owner 2026-09-10).

import type { FtMusicCatalog } from './music-resolver';

const FT_MUSIC_CATALOG_ENDPOINT = 'options/api/music_catalog_inc.php';
// A bucket named by saved state but absent from our copy means an ingest landed after the page loaded.
// Refetch at most this often, so a bucket that genuinely does not exist cannot turn into a request loop.
const FT_MUSIC_CATALOG_MISS_THROTTLE_MS = 60_000;

let ftMcCatalog: FtMusicCatalog | null = null;
let ftMcInflight: Promise<FtMusicCatalog | null> | null = null;
let ftMcLastMissRefetch = 0;

function ftMcDomRev(): string {
    const el = document.querySelector('[data-ft-music-catalog-rev]');
    return el ? String(el.getAttribute('data-ft-music-catalog-rev') || '').trim() : '';
}

function ftMcValid(value: unknown): value is FtMusicCatalog {
    if (!value || typeof value !== 'object') return false;
    const c = value as Partial<FtMusicCatalog>;
    return c.schema === 1 && typeof c.rev === 'string' && typeof c.cdn === 'string'
        && typeof c.crossfade_ms === 'number' && typeof c.creator_crossfade_ms === 'number'
        && !!c.buckets && typeof c.buckets === 'object';
}

async function ftMcLoad(rev: string): Promise<FtMusicCatalog | null> {
    const base = String((window as unknown as { BASE_URL?: string }).BASE_URL || '');
    const url = base + FT_MUSIC_CATALOG_ENDPOINT + (rev ? '?rev=' + encodeURIComponent(rev) : '');
    const res = await fetch(url, { cache: rev ? 'default' : 'no-cache' });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    return ftMcValid(data) ? data : null;
}

/** The catalog, loading it on first use. Null when it cannot be had (the player then leaves music alone). */
export function ftMusicCatalog(): Promise<FtMusicCatalog | null> {
    if (ftMcCatalog) return Promise.resolve(ftMcCatalog);
    if (!ftMcInflight) {
        ftMcInflight = ftMcLoad(ftMcDomRev())
            .catch(() => null)
            .then((c) => {
                ftMcInflight = null;
                if (c) ftMcCatalog = c;
                return c;
            });
    }
    return ftMcInflight;
}

let ftMcLastEnsured = '';

/**
 * Move to the revision the latest campaign push names (`computed.campaign.music_catalog_rev`) — a
 * MusicForge ingest landed after this page loaded. Fetched at `?rev=`, so it is browser-cacheable, and
 * each revision is attempted once. Resolves to the new copy, or null when nothing changed.
 */
export async function ftMusicCatalogEnsureRev(rev: string): Promise<FtMusicCatalog | null> {
    if (!rev || (ftMcCatalog && ftMcCatalog.rev === rev) || rev === ftMcLastEnsured) return null;
    ftMcLastEnsured = rev;
    const next = await ftMcLoad(rev).catch(() => null);
    if (!next) return null;
    ftMcCatalog = next;
    return next;
}

/** Refetch the CURRENT catalog after a miss. Resolves to the new copy, or null when nothing changed. */
export async function ftMusicCatalogRefreshOnMiss(): Promise<FtMusicCatalog | null> {
    const now = Date.now();
    if (now - ftMcLastMissRefetch < FT_MUSIC_CATALOG_MISS_THROTTLE_MS) return null;
    ftMcLastMissRefetch = now;
    const fresh = await ftMcLoad('').catch(() => null);
    if (!fresh || (ftMcCatalog && fresh.rev === ftMcCatalog.rev)) return null;
    ftMcCatalog = fresh;
    return fresh;
}
